import { useMemo } from 'react';

import dayjs from 'dayjs';

import { useInViewport } from '@cast/shared-layout-ui';
import type { ConvertableToDateTime } from '@cast/shared-types';
import type { CostReportClusterResourceUsage } from '@cast/types';

import {
  useClusterOverviewQuery,
  useReportCapabilitiesQuery,
  useResourceUsageQuery,
  toBigNumberTypes,
  type CostReportResourceUsageNormalized,
} from '@cast/shared-cost-data-access';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import {
  averageResourceValues,
  fillTimeSeriesGaps,
  getClusterOverviewPrimarySection,
  isStorageReportsSupported,
  mapClusterOverviewTimeseriesToResourceChartDatapoints,
  mapResourceDatapointsToChartPoints,
  type ResourceType,
  type ResourceUtilizationDatapoint,
} from '@cast/features-cluster-shared-utils';

import { useClusterDashboardOverviewQueryArgs } from './useClusterDashboardOverviewQueryArgs';

const createOverviewResourceFiller =
  () =>
  (timestamp: ConvertableToDateTime): ResourceUtilizationDatapoint => ({
    timestamp: dayjs(timestamp).toISOString(),
    provisioned: null,
    requested: null,
    used: null,
  });

export const useClusterDashboardResourceUtilizationData = (
  resourceType: ResourceType = 'cpu'
) => {
  const inView = useInViewport();
  const { cluster } = useCluster();
  const { startTime, endTime, stepSeconds, range } =
    useClusterDashboardOverviewQueryArgs();
  const isGpu = resourceType === 'gpu';

  const {
    data: overview,
    isLoading: overviewLoading,
    error: overviewError,
    refetch: refetchOverview,
  } = useClusterOverviewQuery({
    clusterId: cluster.id,
    startTime,
    endTime,
    stepSeconds,
    enabled: inView && !isGpu,
  });

  const {
    resourceUsage,
    isLoading: resourceUsageLoading,
    error: resourceUsageError,
    refetch: refetchResourceUsage,
  } = useResourceUsageQuery({
    clusterId: cluster.id,
    startTime,
    endTime,
    stepSeconds,
    enabled: inView && isGpu,
  });

  const {
    data: reportCapabilities,
    isLoading: capabilitiesLoading,
    error: capabilitiesError,
    refetch: refetchCapabilities,
  } = useReportCapabilitiesQuery({
    clusterId: cluster.id,
    startTime,
    endTime,
    enabled: inView,
  });

  const chartDatapoints = useMemo(() => {
    const section = getClusterOverviewPrimarySection(overview);
    return mapClusterOverviewTimeseriesToResourceChartDatapoints(
      section?.resourceTimeseries
    );
  }, [overview]);

  const gpuDatapoints = useMemo((): ResourceUtilizationDatapoint[] => {
    const normalized =
      resourceUsage?.map((item) => ({
        ...toBigNumberTypes<
          CostReportClusterResourceUsage,
          CostReportResourceUsageNormalized[number]
        >(item, ['timestamp']),
        timestamp: item.timestamp!,
      })) ?? [];

    return normalized.map((item) => ({
      timestamp: item.timestamp,
      provisioned: item.gpuAllocatable?.toNumber() ?? null,
      requested: item.gpuRequested?.toNumber() ?? null,
      used: item.gpuUsed?.toNumber() ?? null,
    }));
  }, [resourceUsage]);

  const activeDatapoints = useMemo(() => {
    if (isGpu) {
      return gpuDatapoints;
    }
    if (resourceType === 'memory') {
      return chartDatapoints.memory.datapoints;
    }
    if (resourceType === 'storage') {
      return chartDatapoints.storage.datapoints.map((point) => ({
        timestamp: point.timestamp,
        provisioned: point.provisioned,
        requested: point.requested,
        used: null,
      }));
    }
    return chartDatapoints.cpu.datapoints;
  }, [chartDatapoints, gpuDatapoints, isGpu, resourceType]);

  const datapointsWithoutGaps = useMemo(() => {
    if (!activeDatapoints.length) {
      return [];
    }

    return fillTimeSeriesGaps({
      dateRange: range,
      timeSeries: activeDatapoints,
      filler: createOverviewResourceFiller(),
      unit: 'second',
      step: stepSeconds,
    });
  }, [activeDatapoints, range, stepSeconds]);

  const chartData = useMemo(
    () => mapResourceDatapointsToChartPoints(datapointsWithoutGaps),
    [datapointsWithoutGaps]
  );

  const averages = useMemo(
    () => averageResourceValues(datapointsWithoutGaps),
    [datapointsWithoutGaps]
  );

  return {
    chartData,
    averages,
    isLoading: isGpu
      ? resourceUsageLoading || capabilitiesLoading
      : overviewLoading,
    hasError: isGpu
      ? !!resourceUsageError || !!capabilitiesError
      : !!overviewError,
    showGpu: reportCapabilities?.gpuMetricsAvailable || false,
    showStorage: isStorageReportsSupported(cluster),
    refetch: () => {
      if (isGpu) {
        refetchResourceUsage();
        refetchCapabilities();
        return;
      }
      refetchOverview();
    },
  };
};
